import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Row, Col, Table, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Loading } from '@rd-web-markets/shared/dist/util';
import claimExternalWorkersService from '../../services/claim/claim_external_workers.service';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch } from 'react-redux';
import ExternalWorkerRow from './ExternalWorkerRow';
import { useSelector } from 'react-redux';
import ExternalWorkersRowHeader from './ExternalWorkersRowHeader';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import ExternalWorkersTotalsRow from './ExternalWorkersTotalsRow';
import CostFragmentsTable from '../costFragments/CostFragmentsTable';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
import { CheckboxFormFieldRow } from '@rd-web-markets/shared/dist/util/form';
import CostSectionRateReduction from '../costRateReductions/CostSectionRateReduction';
export const ExternalWorkersSection = _ref => {
  let {
    accountType,
    claim,
    setClaim,
    refreshClaim,
    debouncedSaveClaim,
    saveClaim,
    showCostFragmentsTable,
    allProjects
  } = _ref;
  const [claimExternalWorkers, setClaimExternalWorkers] = useState(null);
  const dispatch = useDispatch();
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const claimId = useMemo(() => claim?.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState('cost_information');
  useEffect(() => {
    async function getData() {
      try {
        if (claimId) {
          setClaimExternalWorkers(await claimExternalWorkersService.all(claimId));
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    }
    getData();
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const externalWorker = claimExternalWorkers[index];
    if (externalWorker.id) {
      try {
        await claimExternalWorkersService.delete(externalWorker);
        const newExternalWorkers = await claimExternalWorkersService.all(claim.id);
        setClaimExternalWorkers(newExternalWorkers);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newExternalWorkers = [...claimExternalWorkers];
      newExternalWorkers.splice(index, 1);
      setClaimExternalWorkers(newExternalWorkers);
    }
  };
  const handleAdd = externalWorker => {
    setClaimExternalWorkers([...claimExternalWorkers, externalWorker]);
  };
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  const userUnauthorizedToEdit = !hasPermissions();
  const showRateReductionTabs = showCostFragmentsTable && claim.is_external_workers_rate_reduction_required;
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !claimExternalWorkers) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "external_workers",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  })), showCostFragmentsTable && /*#__PURE__*/React.createElement(CheckboxFormFieldRow, {
    fieldName: 'is_external_workers_rate_reduction_required',
    label: 'is_rate_reduction_required',
    value: claim.is_external_workers_rate_reduction_required,
    onChange: e => {
      const updatedClaim = saveClaim({
        ...claim,
        is_external_workers_rate_reduction_required: e.target.checked
      });
      setClaim({
        ...claim,
        ...updatedClaim
      });
    },
    disabled: claim.financials_locked || !hasPermissions()
  }), !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, claimExternalWorkers.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(ExternalWorkersRowHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, claimExternalWorkers.map((external_worker, index) => {
    return /*#__PURE__*/React.createElement(ExternalWorkerRow, {
      key: index,
      index: index,
      sortedClaimProjectReports: sortedClaimProjectReports,
      headerScrollPosition: headerScrollPosition,
      claim: claim,
      handleDelete: () => handleDelete(index),
      claimExternalWorkers: claimExternalWorkers,
      setClaimExternalWorkers: setClaimExternalWorkers,
      refreshClaim: refreshClaim,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(ExternalWorkersTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_employee_rnd_apportionments: []
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "add"), " Add Row")))), showCostFragmentsTable && !showRateReductionTabs && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::ClaimExternalWorker",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  }), showRateReductionTabs && /*#__PURE__*/React.createElement(Tabs, {
    defaultActiveKey: "cost_information",
    className: "mb-3",
    onSelect: setActiveTabKey,
    activeKey: activeTabKey
  }, /*#__PURE__*/React.createElement(Tab, {
    eventKey: "cost_information",
    title: "Cost Information"
  }, activeTabKey === 'cost_information' && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::ClaimExternalWorker",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement(Tab, {
    eventKey: "rate_reduction",
    title: "Rate Reduction"
  }, activeTabKey === 'rate_reduction' && /*#__PURE__*/React.createElement(CostSectionRateReduction, {
    claim: claim,
    costType: 'UkSection::ClaimExternalWorker'
  }))), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-start"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "outline-primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/subcontractors`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_left"), " Back")), /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-end"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/consumables`
  }, "Continue ", /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_right")))));
};